// ============================================================================
// remote/inputCapture.ts — ЕДИНЫЙ захват ввода для вьюера «Удалённого доступа»
// (мышь/клавиатура/тач) над АБСТРАКТНЫМ приёмником событий (SRP). Не знает
// транспорт: LiveKit-путь прокидывает publishData, контрактный — WS JSON.
// Контракт событий: plans/remote-protocol-contract.md §4.1.
//
// Возможности (2026-10-11):
//  • Pointer Lock — курсор «залипает», движение ОТНОСИТЕЛЬНОЕ (mouse_move_rel),
//    кнопки/колесо — rel=true (в текущей позиции курсора хоста);
//  • Keyboard Lock — в fullscreen системные клавиши (Alt+Tab и т.п.) уходят на
//    удалённый ПК (Chromium, feature-detect);
//  • расширенная карта клавиш и комбинации (общие данные — lib/remote/inputKeys);
//  • тач: Pointer Events унифицируют мышь/палец/перо — тап/свайп как клик/протяжка.
// ============================================================================

import { charToVk, KEY_SCANCODES, keyboardLockApi } from '@/lib/remote/inputKeys';
import type { RemoteInputEvent } from '@/lib/remote/remoteSession';

export interface InputCaptureState {
  pointerLocked: boolean;
  keyboardLocked: boolean;
  fullscreen: boolean;
}

export interface StartInputCaptureOptions {
  /** Элемент, по которому принимается ввод (сцена с изображением экрана). */
  element: HTMLElement;
  /** Приёмник событий (одно событие за вызов; движение — уже коалесцировано rAF). */
  send: (ev: RemoteInputEvent) => void;
  /** Смена состояния захвата (курсор/системные клавиши/полный экран). */
  onState?: (state: InputCaptureState) => void;
}

export interface InputCaptureHandle {
  stop: () => void;
  toggleFullscreen: () => void;
}

export function startInputCapture({ element, send, onState }: StartInputCaptureOptions): InputCaptureHandle {
  let raf = 0;
  let pendingMove: RemoteInputEvent | null = null;
  let keyboardLocked = false;
  const lockedRef = { current: false };
  const kb = keyboardLockApi();

  const state = (): InputCaptureState => ({
    pointerLocked: lockedRef.current,
    keyboardLocked,
    fullscreen: !!document.fullscreenElement,
  });
  const emitState = () => onState?.(state());

  const flush = () => {
    raf = 0;
    if (pendingMove) {
      send(pendingMove);
      pendingMove = null;
    }
  };
  const dispatch = (ev: RemoteInputEvent) => {
    if (ev.type === 'mouse_move' || ev.type === 'mouse_move_rel') {
      pendingMove = ev;
      if (!raf) raf = window.requestAnimationFrame(flush);
      return;
    }
    send(ev);
  };
  const norm = (e: { clientX: number; clientY: number }) => {
    const r = element.getBoundingClientRect();
    return {
      x: Math.min(1, Math.max(0, (e.clientX - r.left) / Math.max(1, r.width))),
      y: Math.min(1, Math.max(0, (e.clientY - r.top) / Math.max(1, r.height))),
    };
  };

  const onMove = (e: PointerEvent) => {
    if (lockedRef.current) {
      dispatch({ type: 'mouse_move_rel', dx: e.movementX, dy: e.movementY });
    } else {
      dispatch({ type: 'mouse_move', ...norm(e) });
    }
  };
  const onDown = (e: PointerEvent) => {
    const button = e.button === 2 ? 'right' : e.button === 1 ? 'middle' : 'left';
    // Клик по экрану = вход в Pointer Lock (курсор «залипает» на удалённом).
    if (!lockedRef.current && typeof element.requestPointerLock === 'function' && e.button === 0) {
      try {
        element.requestPointerLock().catch(() => { /* браузер отказал — работаем без лока */ });
      } catch { /* нет API — работаем без лока */ }
    }
    if (lockedRef.current) dispatch({ type: 'mouse_button', rel: true, button, down: true });
    else dispatch({ type: 'mouse_button', ...norm(e), button, down: true });
  };
  const onUp = (e: PointerEvent) => {
    const button = e.button === 2 ? 'right' : e.button === 1 ? 'middle' : 'left';
    if (lockedRef.current) dispatch({ type: 'mouse_button', rel: true, button, down: false });
    else dispatch({ type: 'mouse_button', ...norm(e), button, down: false });
  };
  const onWheel = (e: WheelEvent) => {
    const delta = e.deltaY > 0 ? -120 : 120;
    if (lockedRef.current) dispatch({ type: 'mouse_wheel', rel: true, delta });
    else dispatch({ type: 'mouse_wheel', ...norm(e), delta });
    e.preventDefault();
  };
  const onContext = (e: Event) => e.preventDefault();

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.repeat) return;
    const sc = KEY_SCANCODES[e.key];
    if (sc) {
      dispatch({ type: 'key_scancode', code: sc, down: true });
      e.preventDefault();
      return;
    }
    if (e.key.length === 1) {
      if (e.ctrlKey || e.altKey || e.metaKey) {
        const vk = charToVk(e.key);
        if (vk) { dispatch({ type: 'key_scancode', code: vk, down: true }); e.preventDefault(); }
      } else {
        dispatch({ type: 'key_text', text: e.key });
        e.preventDefault();
      }
    }
  };
  const onKeyUp = (e: KeyboardEvent) => {
    const sc = KEY_SCANCODES[e.key];
    if (sc) { dispatch({ type: 'key_scancode', code: sc, down: false }); return; }
    if (e.key.length === 1 && (e.ctrlKey || e.altKey || e.metaKey)) {
      const vk = charToVk(e.key);
      if (vk) dispatch({ type: 'key_scancode', code: vk, down: false });
    }
  };

  const onLockChange = () => {
    lockedRef.current = document.pointerLockElement === element;
    emitState();
  };

  // Keyboard Lock работает ТОЛЬКО в fullscreen (браузерное ограничение).
  const applyKeyboardLock = () => {
    if (!kb || typeof kb.lock !== 'function') { emitState(); return; }
    const wantLock = !!(document.fullscreenElement && lockedRef.current);
    try {
      if (wantLock) {
        const r = kb.lock?.();
        if (r && typeof r.then === 'function') {
          r.then(() => { keyboardLocked = true; emitState(); }).catch(() => { keyboardLocked = false; emitState(); });
        } else {
          keyboardLocked = true;
          emitState();
        }
      } else if (typeof kb.unlock === 'function') {
        kb.unlock();
        keyboardLocked = false;
        emitState();
      }
    } catch {
      keyboardLocked = false;
      emitState();
    }
  };

  const toggleFullscreen = () => {
    try {
      if (document.fullscreenElement) {
        void document.exitFullscreen?.();
      } else {
        void element.requestFullscreen?.();
      }
    } catch {
      /* API недоступен — работаем без fullscreen */
    }
  };

  element.addEventListener('pointermove', onMove);
  element.addEventListener('pointerdown', onDown);
  element.addEventListener('pointerup', onUp);
  element.addEventListener('wheel', onWheel, { passive: false });
  element.addEventListener('contextmenu', onContext);
  document.addEventListener('keydown', onKeyDown);
  document.addEventListener('keyup', onKeyUp);
  document.addEventListener('pointerlockchange', onLockChange);
  document.addEventListener('fullscreenchange', applyKeyboardLock);
  element.tabIndex = 0;
  element.focus();
  const prevTouchAction = element.style.touchAction;
  const prevCursor = element.style.cursor;
  element.style.touchAction = 'none';
  element.style.cursor = 'crosshair';
  emitState();

  return {
    toggleFullscreen,
    stop: () => {
      if (raf) window.cancelAnimationFrame(raf);
      element.removeEventListener('pointermove', onMove);
      element.removeEventListener('pointerdown', onDown);
      element.removeEventListener('pointerup', onUp);
      element.removeEventListener('wheel', onWheel);
      element.removeEventListener('contextmenu', onContext);
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('keyup', onKeyUp);
      document.removeEventListener('pointerlockchange', onLockChange);
      document.removeEventListener('fullscreenchange', applyKeyboardLock);
      if (document.pointerLockElement === element && typeof document.exitPointerLock === 'function') {
        document.exitPointerLock();
      }
      try { kb?.unlock?.(); } catch { /* не критично */ }
      keyboardLocked = false;
      lockedRef.current = false;
      element.style.touchAction = prevTouchAction;
      element.style.cursor = prevCursor;
      onState?.({ pointerLocked: false, keyboardLocked: false, fullscreen: !!document.fullscreenElement });
    },
  };
}
